import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  GetTenantDocument,
  UpdateTenantMemberRoleDocument,
  type UpdateTenantMemberRoleMutation,
  type UpdateTenantMemberRoleMutationVariables,
} from "~/features/admin/tenants/gqls";
import type { EditableTenantMembershipRole } from "~/features/admin/tenants/types";
import { translateError } from "~/lib/errors";

/** Change one existing tenant membership between member and manager. */
export function useTenantUpdateMemberRole() {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [updateMemberRole] = useMutation<UpdateTenantMemberRoleMutation, UpdateTenantMemberRoleMutationVariables>(
    UpdateTenantMemberRoleDocument,
    { refetchQueries: [GetTenantDocument], awaitRefetchQueries: true },
  );

  const submit = useCallback(
    async (tenantId: string, userId: string, role: EditableTenantMembershipRole) => {
      setProcessing(true);
      try {
        const res = await updateMemberRole({ variables: { input: { id: tenantId, userId, role } } });
        match(res.data?.cnutUpdateTenantMemberRole)
          .with({ __typename: "ValidationError" }, (e) => toast.error(e.fields.map((f) => f.message).join(", ")))
          .with({ __typename: "TenantUpdateMemberRoleOutput" }, () => {
            toast.success(t("model.tenant.memberRoleUpdatedSuccessfully"));
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [updateMemberRole, t],
  );

  return { submit, processing };
}
